import CircularProgress from '@material-ui/core/CircularProgress';
import PropTypes from 'prop-types';
import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import { SubmitButton } from './SubmitButton';

const styles = {
  footer: {
    bottom: 0,
    left: 0,
    position: 'relative',
    paddingTop: 32,
    textAlign: 'center',
    width: '100%',
  },
  loader: {
    position: 'absolute',
  },
  submitWrapper: {
    marginBottom: 10,
  },
};

class AuthenticationFormComponent extends React.Component {
  static propTypes = {
    children: PropTypes.node.isRequired,
    classes: PropTypes.objectOf(PropTypes.string).isRequired,
    defaultValues: PropTypes.objectOf(PropTypes.string), // default values for fields.
    errors: PropTypes.objectOf(PropTypes.string),
    footer: PropTypes.node,
    noValidate: PropTypes.bool,
    onSubmit: PropTypes.func.isRequired,
    submitLabel: PropTypes.string.isRequired,
    values: PropTypes.objectOf(PropTypes.string), // read-only values for fields.
    disableSubmit: PropTypes.bool,
  };

  static defaultProps = {
    defaultValues: null,
    errors: null,
    footer: null,
    noValidate: false,
    values: null,
    disableSubmit: false,
  };

  state = {
    loading: false, // used to show/hide spinner.
    submitted: false, // used to show/hide error on form input.
  };

  static getDerivedStateFromProps(props, state) {
    return { submitted: state.submitted || !!props.defaultValues };
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  fields = [];
  mounted = true;

  handleFieldChange = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  handleFormSubmit = async (e) => {
    e.preventDefault();

    const data = Object.keys(this.fields).reduce(
      (obj, field) => ({
        ...obj,
        [field]: this.state[field],
      }),
      {},
    );

    this.setState({ loading: true });
    await this.props.onSubmit(data);

    // The following statement might get executed on an unmounted component depending on the
    // onSubmit actions. This `if` is to avoid triggering that warning.
    if (this.mounted) {
      this.setState({ loading: false, submitted: true });
    }
  };

  isSubmitDisabled() {
    const getCompareFields = Object.keys(this.fields).filter(
      (fieldName) =>
        this.fields[fieldName].inputProps && this.fields[fieldName].inputProps.datacompare,
    );
    const requiredFieldIsMissingValue = Object.keys(this.fields).some((fieldName) => {
      return this.fields[fieldName].required && !this.state[fieldName];
    });

    if (getCompareFields.length > 0) {
      let allCompareFieldsHaveSameValue = getCompareFields
        .map((compareField) => this.state[compareField])
        .every((v, i, arr) => v === arr[0]);
      return !allCompareFieldsHaveSameValue || requiredFieldIsMissingValue;
    } else {
      return requiredFieldIsMissingValue;
    }
  }

  render() {
    const { classes, defaultValues, errors, footer, submitLabel, values } = this.props;
    const { loading, submitted } = this.state;
    const hasErrors = submitted && errors;

    const children = React.Children.map(this.props.children, (child) => {
      if (!child) {
        return child;
      }

      const { name, select, type, required, inputProps } = child.props;

      // Any other way of checking the child's a TextField is welcome.
      if (name && (type || select)) {
        const value = values && values[name];

        if (!this.fields[name]) {
          if (type) {
            this.state[name] = value || (defaultValues && defaultValues[name]) || '';
          }
          this.fields[name] = {
            required,
            inputProps,
          };
        }

        return React.cloneElement(child, {
          disabled: !!value,
          error: hasErrors && errors[name],
          helperText: hasErrors ? errors[name] : null,
          onChange: this.handleFieldChange,
          value: value || this.state[name],
        });
      }

      return child;
    });

    return (
      <form onSubmit={this.handleFormSubmit} noValidate={this.props.noValidate}>
        {children}
        <div className={classes.footer}>
          <div className={classes.submitWrapper}>
            <SubmitButton disabled={loading || this.isSubmitDisabled() || this.props.disableSubmit}>
              {submitLabel}
              {loading && <CircularProgress className={classes.loader} size={25} />}
            </SubmitButton>
          </div>
          {footer}
        </div>
      </form>
    );
  }
}

export const AuthenticationForm = withStyles(styles)(AuthenticationFormComponent);
